import { useState, useEffect, useCallback } from "react"; // <-- Import useCallback
import { getPublicInfo, putInfo } from "utils/api/private/Info.api";
import {
  extractErrorMessage,
  PaginatedApiResponse,
  Severity,
} from "utils/helper/ApiResponses";
import { Provider, QAType } from "utils/types/Provider.types";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
}

export function useProviderManager() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentQA, setCurrentQA] = useState<QAType | null>(null);

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );
  const [expandedQA, setExpandedQA] = useState<string | null>(null);

  // Form State
  const [editDescription, setEditDescription] = useState<string>("");
  const [editImage, setEditImage] = useState<string>("");
  const [editVideoUrl, setEditVideoUrl] = useState<string>("");
  const [editExternalUrl, setEditExternalUrl] = useState<string>("");
  const [editAdditionalInfo, setEditAdditionalInfo] = useState<string>("");

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // --- FIX 1: Wrap handleSnackbarOpen in useCallback ---
  const handleSnackbarOpen = useCallback(
    (message: string, severity: Severity) => {
      setSnackbar({ open: true, message, severity });
    },
    []
  ); // State setters are stable, so the dependency array is empty

  // --- FIX 1: Wrap fetchProviders in useCallback ---
  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPublicInfo<Provider>(["providers"], {
        page: 1,
        limit: 100,
        sortBy: "name",
        order: "asc",
      });

      const providersList = (result.data as PaginatedApiResponse<Provider>)
        .data;
      setProviders(providersList || []);
    } catch (err: any) {
      handleSnackbarOpen(extractErrorMessage(err).message, "error");
    } finally {
      setLoading(false);
    }
  }, [handleSnackbarOpen]); // Add its dependency

  // --- FIX 1: Add the stable fetchProviders to the dependency array ---
  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const handleSelectProvider = (provider: Provider) => {
    if (selectedProvider?.name === provider.name) {
      setSelectedProvider(null); // Deselect
    } else {
      setSelectedProvider(provider);
      setEditDescription(provider.description || "");
      setEditImage(provider.image || provider.url || ""); // Use new 'image' field
      setEditVideoUrl(provider.videoUrl || "");
      setEditExternalUrl(provider.externalUrl || "");
      setEditAdditionalInfo(provider.additionalInfo || ""); // Use new 'additionalInfo'
      setExpandedQA(null);
    }
  };

  const handleSaveProviderDetails = async () => {
    if (!selectedProvider) return;
    setIsSaving(true);
    try {
      const providerData: Partial<Provider> = {
        name: selectedProvider.name,
        description: editDescription,
        image: editImage, // Send the updated image field
        url: editImage, // Also update 'url' for compatibility
        videoUrl: editVideoUrl,
        externalUrl: editExternalUrl,
        additionalInfo: editAdditionalInfo,
        qas: selectedProvider.qas || [], // Ensure qas is an array
      };

      await putInfo(["providers"], providerData);

      handleSnackbarOpen("Détails du fournisseur sauvegardés!", "success");

      // Update local state immediately
      const updatedProvider = {
        ...selectedProvider,
        ...providerData,
      } as Provider;
      setSelectedProvider(updatedProvider);
      setProviders((prev) =>
        prev.map((p) => (p.name === updatedProvider.name ? updatedProvider : p))
      );
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const updateProviderQAs = async (qas: QAType[]) => {
    if (!selectedProvider) return;

    const providerData = { ...selectedProvider, qas };

    setIsSaving(true);
    try {
      await putInfo(["providers"], providerData);

      // Update state locally for immediate feedback
      setSelectedProvider(providerData);
      setProviders((prev) =>
        prev.map((p) => (p.name === providerData.name ? providerData : p))
      );
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQA = async (qa: QAType) => {
    const updatedQAs = [...(selectedProvider?.qas || [])];
    const index = updatedQAs.findIndex((q) => q.id === qa.id);

    if (index > -1) {
      updatedQAs[index] = qa; // Update
    } else {
      updatedQAs.push({ ...qa, id: `qa_${Date.now()}` }); // Add new
    }

    await updateProviderQAs(updatedQAs);
    handleSnackbarOpen("Q&A sauvegardée.", "success");
    setOpenDialog(false);
    setCurrentQA(null);
  };

  const handleDeleteQA = async (id: string) => {
    if (
      !selectedProvider ||
      // --- FIX 2: Disable the ESLint rule for this line ---
      // eslint-disable-next-line no-restricted-globals
      !confirm("Êtes-vous sûr de vouloir supprimer cette Q&A ?")
    )
      return;

    const updatedQAs = (selectedProvider.qas || []).filter(
      (qa) => qa.id !== id
    );
    await updateProviderQAs(updatedQAs);
    handleSnackbarOpen("Q&A supprimée.", "success");
  };

  const handleOpenAddQADialog = () => {
    setCurrentQA(null);
    setOpenDialog(true);
  };

  const handleOpenEditQADialog = (qa: QAType) => {
    setCurrentQA(qa);
    setOpenDialog(true);
  };

  return {
    providers,
    loading,
    isSaving,
    openDialog,
    currentQA,
    expandedQA,
    selectedProvider,
    snackbar,
    editDescription,
    editImage,
    editVideoUrl,
    editExternalUrl,
    editAdditionalInfo,
    setEditDescription,
    setEditImage,
    setEditVideoUrl,
    setEditExternalUrl,
    setEditAdditionalInfo,
    handleSelectProvider,
    handleSaveProviderDetails,
    handleSaveQA,
    handleDeleteQA,
    setExpandedQA,
    setOpenDialog,
    handleSnackbarClose,
    handleOpenAddQADialog,
    handleOpenEditQADialog,
  };
}
